import React, { useState } from 'react';
import { Scissors, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useNavigate } from 'react-router-dom';

const fmt = (s) => `${Math.floor(s/60)}:${String(Math.floor(s%60)).padStart(2, '0')}`;

/**
 * ChalkButton — opens a modal that cuts a 15-second highlight from any post
 * and reposts it as a "chalk" track. Backend: POST /api/chalks.
 */
export default function ChalkButton({ post }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [startSec, setStartSec] = useState(0);
  const [busy, setBusy] = useState(false);
  const duration = post.duration || 180;
  const maxStart = Math.max(0, duration - 15);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post('/chalks', { post_id: post.id, start_sec: startSec, duration_sec: 15 });
      toast.success('Chalk posted to feed');
      setOpen(false);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chalk failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-[#A1A1AA] hover:text-[#FFB000] flex items-center gap-1.5 text-xs font-bold transition-colors"
        data-testid={`chalk-btn-${post.id}`}
        title="Cut a 15s highlight from this mix"
      >
        <Scissors size={14} /> Chalk
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="chalk-modal">
          <div className="post-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FFB000]">Vinyl Chalk</div>
                <div className="text-white font-heading font-black text-2xl">Cut a 15s highlight</div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#A1A1AA] hover:text-white" data-testid="chalk-close-btn"><X size={18} /></button>
            </div>
            <div className="text-[#A1A1AA] text-xs mb-4">
              Pick the moment. RAW cuts a 15-second slice and posts it as a shareable clip that links back to your original mix.
            </div>
            <label className="block text-xs uppercase tracking-widest text-[#A1A1AA] font-bold mb-2">Start at</label>
            <div className="flex items-center gap-3 mb-4">
              <input
                type="range" min="0" max={maxStart} step="1" value={startSec}
                onChange={e => setStartSec(parseFloat(e.target.value))}
                className="flex-1 accent-[#FFB000]"
                data-testid="chalk-start-range"
              />
              <div className="font-mono text-[#FFB000] font-bold text-sm min-w-[85px] text-right" data-testid="chalk-start-label">
                {fmt(startSec)} → {fmt(startSec + 15)}
              </div>
            </div>
            <button
              onClick={submit}
              disabled={busy}
              className="btn-pill btn-primary w-full flex items-center justify-center gap-2 text-sm disabled:opacity-40"
              data-testid="chalk-submit-btn"
            >
              <Scissors size={14} /> {busy ? 'Cutting…' : 'Post 15-second chalk'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
